import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  INGRESO_POR_DIA_TITULO_DEFAULT,
  INGRESO_POR_DIA_COLUMNS,
  applyIngresoPorDiaColumnLabels,
  formatIngresoPorDiaCell,
  getIngresoPorDiaTotals,
  mergeIngresoPorDiaEncabezado,
  normalizeIngresoPorDiaRow,
} from './ingresoPorDiaReportModel';
import {
  columnWidthsProportionalMm,
  drawEncabezadoReportePdf,
  pdfTableInnerWidthMm,
} from './reporteEncabezadoPdf';

const HEAD_BG   = [15, 47, 89];
const HEAD_TEXT = [255, 255, 255];
const FOOT_BG   = [30, 58, 95];
const SUB_BG    = [248, 250, 252];

export function downloadIngresoPorDiaPdf(rawRows, options = {}) {
  if (!Array.isArray(rawRows)) return;

  const {
    fileBase           = 'ingresos-por-dia',
    encabezadoApi      = null,
    encabezadoFallback = null,
    reporteTitulo      = INGRESO_POR_DIA_TITULO_DEFAULT,
    apiTotales         = null,
    columnas           = null,
    vistaIngreso       = 'dia',
  } = options;

  const cols     = applyIngresoPorDiaColumnLabels(columnas, INGRESO_POR_DIA_COLUMNS);
  const normRows = rawRows.map(normalizeIngresoPorDiaRow);
  const totals   = getIngresoPorDiaTotals(normRows, apiTotales);

  /* En vista "total" sólo se imprime la fila de totales, sin detalle por día */
  const bodyRows = vistaIngreso === 'total' ? [] : normRows;

  const enc = mergeIngresoPorDiaEncabezado(encabezadoApi, encabezadoFallback);
  let nombreDisplay = enc.nombreComercial || '';
  if (enc.sucursalAppend && !nombreDisplay.toUpperCase().includes(enc.sucursalAppend.toUpperCase())) {
    nombreDisplay = nombreDisplay ? `${nombreDisplay} - ${enc.sucursalAppend.toUpperCase()}` : enc.sucursalAppend.toUpperCase();
  }
  const encPdf = {
    ...enc,
    nombreComercial: nombreDisplay || null,
    reporteTitulo,
  };

  /* Landscape A4 para acomodar 10 columnas */
  const doc    = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW  = doc.internal.pageSize.getWidth();
  const margin = 10;

  let y = drawEncabezadoReportePdf(doc, encPdf, pageW, margin, margin, {},
    { omitResponsable: true, omitDomicilio: false, tituloResaltadoRgb: [191, 219, 254] });

  /* Encabezados y cuerpo */
  const head = [cols.map((c) => c.label)];
  const body = bodyRows.map((r) => cols.map((c) => formatIngresoPorDiaCell(c, r[c.id])));

  /* Fila de totales */
  const footRow = cols.map((c, i) => {
    if (i === 0) return 'TOTAL';
    if (!c.total) return '';
    const v = totals[c.id];
    return v != null ? formatIngresoPorDiaCell(c, v) : '—';
  });

  /* Anchos proporcionales: fecha más ancha, money columns iguales */
  const innerW  = pdfTableInnerWidthMm(pageW, margin);
  const weights = [28, 28, 22, 22, 22, 22, 22, 22, 22, 28];
  const widths  = columnWidthsProportionalMm(weights, innerW);

  autoTable(doc, {
    startY: y,
    tableWidth: innerW,
    head,
    body,
    foot: [footRow],
    showFoot: 'lastPage',
    theme: 'plain',
    headStyles: { fillColor: HEAD_BG, textColor: HEAD_TEXT, fontStyle: 'bold', fontSize: 6.5 },
    footStyles: { fillColor: FOOT_BG, textColor: [255, 255, 255], fontStyle: 'bold', lineWidth: { top: 0.3 }, fontSize: 6.5 },
    bodyStyles: { textColor: [0, 0, 0], fontSize: 6.5 },
    styles: { cellPadding: 1.2 },
    columnStyles: Object.fromEntries(
      cols.map((c, i) => [i, { halign: c.align, cellWidth: widths[i] }])
    ),
    margin: { left: margin, right: margin },
    didParseCell: (data) => {
      const i = data.column.index;
      if (data.section === 'head') {
        data.cell.styles.halign = cols[i]?.align || 'left';
        data.cell.styles.fillColor = HEAD_BG;
        data.cell.styles.textColor = HEAD_TEXT;
      }
      if (data.section === 'foot') {
        data.cell.styles.halign = i === 0 ? 'left' : 'right';
      }
      if (data.section === 'body') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? [255, 255, 255] : SUB_BG;
      }
    },
  });

  const totalPages = doc.internal.getNumberOfPages();
  const ph = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(90);
    doc.text(`Generado ${new Date().toLocaleString('es-MX')}`, margin, ph - 6);
    doc.text(`Página ${i} de ${totalPages}`, pageW - margin, ph - 6, { align: 'right' });
    doc.setTextColor(0);
  }

  doc.save(`${fileBase}.pdf`);
}
